import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';
import { nfcLogger } from './NFCLogger';
import { useI18n } from '@/lib/i18n/client';

interface NDEFRecord {
    recordType: string;
    mediaType?: string;
    id?: string;
    data?: DataView;
    encoding?: string;
    lang?: string;
}

interface NDEFMessage {
    records: NDEFRecord[];
}

declare global {
    class NDEFReader {
        scan(): Promise<void>;

        addEventListener(type: 'reading', listener: (event: { message: NDEFMessage; serialNumber: string }) => void): void;
        addEventListener(type: 'readingerror', listener: () => void): void;

        removeEventListener(type: 'reading', listener: (event: { message: NDEFMessage; serialNumber: string }) => void): void;
        removeEventListener(type: 'readingerror', listener: () => void): void;
    }

    interface Window {
        NDEFReader: typeof NDEFReader;
    }
}

export interface UseNFCReaderProps {
    onTagRead: (tagEvent: { tagUid: string; timestamp: Date }) => Promise<void>;
    onError: (err: string | Error) => Promise<void>;
}

export interface UseNFCReaderReturn {
    isScanning: boolean;
    loadingState: 'idle' | 'initializing' | 'requesting-permission' | 'starting-scan' | 'scanning' | 'error';
    statusMessage: string;
    startScanning: () => Promise<void>;
    stopScanning: () => void;
}

export const useNFCReader = (props: UseNFCReaderProps): UseNFCReaderReturn => {
    const { t } = useI18n();
    const { onTagRead, onError } = props;

    const [isScanning, setIsScanning] = useState<boolean>(false);
    const [loadingState, setLoadingState] = useState<'idle' | 'initializing' | 'requesting-permission' | 'starting-scan' | 'scanning' | 'error'>('idle');
    const [statusMessage, setStatusMessage] = useState<string>('');

    const ndefReaderRef = useRef<NDEFReader | null>(null);
    const savedTagReadCallback = useRef(onTagRead);
    const savedErrorCallback = useRef(onError);

    useIsomorphicLayoutEffect(() => {
        savedTagReadCallback.current = onTagRead;
    }, [onTagRead]);

    useIsomorphicLayoutEffect(() => {
        savedErrorCallback.current = onError;
    }, [onError]);

    const handleNFCReading = useCallback(({ message, serialNumber }: { message: NDEFMessage; serialNumber: string }) => {
        const tagUid = serialNumber;
        nfcLogger.logSuccess('nfc_tag_read', {
            tagUid,
            messageRecords: message.records.length,
            timestamp: new Date().toISOString()
        });

        savedTagReadCallback.current({
            tagUid,
            timestamp: new Date()
        });
    }, []);

    const handleNFCReadingError = useCallback(() => {
        const error = new Error(t('nfc.errors.reading'));
        nfcLogger.logError(error, 'nfc_reading_error');
        savedErrorCallback.current?.(t('nfc.errors.reading'));
    }, [t]);

    async function startScanning(): Promise<void> {
        nfcLogger.logOperation('start_nfc_reading');
        setLoadingState('initializing');
        setStatusMessage(t('nfc.loading.initializing'));

        try {
            setLoadingState('requesting-permission');
            setStatusMessage(t('nfc.loading.requestingPermission'));

            ndefReaderRef.current = new NDEFReader();

            setLoadingState('starting-scan');
            setStatusMessage(t('nfc.loading.startingScan'));

            await ndefReaderRef.current.scan();

            setIsScanning(true);
            setLoadingState('scanning');
            setStatusMessage(t('nfc.status.ready'));
            nfcLogger.logSuccess('nfc_scan_started');

            ndefReaderRef.current.addEventListener('reading', handleNFCReading);
            ndefReaderRef.current.addEventListener('readingerror', handleNFCReadingError);

        } catch (error) {
            setLoadingState('error');
            let errorMessage = t('nfc.errors.unknown');

            if (error instanceof DOMException) {
                nfcLogger.logError(error, 'nfc_scan_start_failure');

                switch (error.name) {
                    case 'AbortError':
                        errorMessage = t('nfc.dom.AbortError');
                        break;
                    case 'InvalidStateError':
                        errorMessage = t('nfc.dom.InvalidStateError');
                        break;
                    case 'NotAllowedError':
                        errorMessage = t('nfc.dom.NotAllowedError');
                        break;
                    case 'NotSupportedError':
                        errorMessage = t('nfc.dom.NotSupportedError');
                        break;
                    case 'NotReadableError':
                        errorMessage = t('nfc.dom.NotReadableError');
                        break;
                    default:
                        errorMessage = t('nfc.errors.withDetail', { message: error.message });
                }
            }

            setIsScanning(false);
            setStatusMessage(errorMessage);
            savedErrorCallback.current?.(errorMessage);
        }
    }

    function stopScanning(): void {
        nfcLogger.logOperation('cleanup_nfc_reader');

        if (ndefReaderRef.current) {
            ndefReaderRef.current.removeEventListener('reading', handleNFCReading);
            ndefReaderRef.current.removeEventListener('readingerror', handleNFCReadingError);
            nfcLogger.logSuccess('nfc_event_listeners_removed');
            ndefReaderRef.current = null;
        }

        setIsScanning(false);
        setLoadingState('idle');
        setStatusMessage('');
    }

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (ndefReaderRef.current) {
                ndefReaderRef.current.removeEventListener('reading', handleNFCReading);
                ndefReaderRef.current.removeEventListener('readingerror', handleNFCReadingError);
                ndefReaderRef.current = null;
            }
        };
    }, [handleNFCReading, handleNFCReadingError]);

    return {
        isScanning,
        loadingState,
        statusMessage,
        startScanning,
        stopScanning
    };
};
